import { useRegisterSW } from 'virtual:pwa-register/react';

import { PwaUpdatePrompt } from './PwaUpdatePrompt';

/**
 * PwaUpdater — the thin container that registers the service worker and
 * surfaces the "new version available" prompt.
 *
 * Registration lives here, in bundled application code, on purpose: the
 * production CSP is `script-src 'self' 'unsafe-eval'` with no 'unsafe-inline',
 * so the plugin's default inline registration script would be blocked in prod
 * (the SW would silently never register). `injectRegister: null` in
 * vite.config.ts suppresses that inline script; calling useRegisterSW from
 * this bundled module registers from a 'self' source instead.
 *
 * `registerType: 'prompt'` (vite.config.ts) means a new SW waits rather than
 * taking over silently — `needRefresh` flips true when one is waiting, and
 * `updateServiceWorker(true)` activates it and reloads the page into the new
 * build. The user taps Reload; nothing reloads under an in-progress edit.
 *
 * This container is intentionally not unit-tested — it depends on the
 * virtual:pwa-register/react module and a real service worker, which need a
 * browser. The presentational PwaUpdatePrompt carries the test + story; the
 * build gate and on-device verification cover the registration wiring.
 */
export function PwaUpdater() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  return (
    <PwaUpdatePrompt
      show={needRefresh}
      onReload={() => void updateServiceWorker(true)}
      onDismiss={() => setNeedRefresh(false)}
    />
  );
}
